import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
    ArrowRight,
    ArrowUpRight,
    CheckCircle2,
    ClipboardList,
    FileText,
    LayoutDashboard,
    Loader2,
    MapPin,
    MessagesSquare,
    ShieldCheck,
    UserCheck,
    Zap,
} from "lucide-react";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { IMAGES } from "@/data/content";
import { useLang } from "@/i18n/LangContext";

const AMOUNTS = [100, 500, 1000, 5000];
const STAT_VALUES = ["62k", "47", "KES 18M"];
const TOOL_ICONS = [UserCheck, MapPin, MessagesSquare, ClipboardList];
const FEATURE_ICONS = [FileText, LayoutDashboard, Zap, ShieldCheck];

const DonationDemo = ({ c }) => {
    const [step, setStep] = useState(0);
    const [amount, setAmount] = useState(500);

    useEffect(() => {
        if (step !== 1) return;
        const timer = setTimeout(() => setStep(2), 2800);
        return () => clearTimeout(timer);
    }, [step]);

    return (
        <div className="overflow-hidden rounded-lg border border-border bg-card" data-testid="donation-demo">
            <div className="flex items-center justify-between border-b border-border bg-secondary/60 px-5 py-3">
                <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    Sauti Yetu 2027 · {c.receipt}
                </p>
                <span className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-teal">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-teal animate-pulse-dot" />
                    Live demo
                </span>
            </div>

            <div className="p-6 sm:p-8">
                <AnimatePresence mode="wait">
                    {step === 0 && (
                        <motion.div
                            key="step0"
                            initial={{ opacity: 0, x: 24 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -24 }}
                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        >
                            <p className="font-heading text-lg font-semibold tracking-tight" data-testid="donation-step1-title">{c.step1}</p>
                            <div className="mt-5 grid grid-cols-2 gap-2.5">
                                {AMOUNTS.map((a) => (
                                    <button
                                        key={a}
                                        type="button"
                                        onClick={() => setAmount(a)}
                                        aria-pressed={amount === a}
                                        data-testid={`donation-amount-${a}`}
                                        className={`rounded-md border px-4 py-3.5 font-heading text-lg font-bold transition-all duration-200 active:scale-[0.97] ${
                                            amount === a
                                                ? "border-primary bg-primary text-primary-foreground"
                                                : "border-border bg-background text-foreground hover:border-primary"
                                        }`}
                                    >
                                        KES {a.toLocaleString()}
                                    </button>
                                ))}
                            </div>
                            <button
                                type="button"
                                onClick={() => setStep(1)}
                                data-testid="donation-continue-button"
                                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-terracotta px-6 py-3.5 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.98]"
                            >
                                {c.stepContinue} — KES {amount.toLocaleString()}
                                <ArrowRight className="h-4 w-4" />
                            </button>
                        </motion.div>
                    )}

                    {step === 1 && (
                        <motion.div
                            key="step1"
                            initial={{ opacity: 0, x: 24 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -24 }}
                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            className="flex flex-col items-center py-6 text-center"
                            data-testid="donation-step2"
                        >
                            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-accent">
                                <Loader2 className="h-6 w-6 animate-spin text-primary dark:text-teal" />
                            </div>
                            <p className="mt-5 font-heading text-lg font-semibold tracking-tight">{c.step2Title}</p>
                            <p className="mt-2 max-w-xs text-sm leading-relaxed text-muted-foreground">{c.step2Copy}</p>
                            <div className="mt-5 h-1 w-40 overflow-hidden rounded-full bg-secondary">
                                <motion.div
                                    className="h-full bg-teal"
                                    initial={{ width: "0%" }}
                                    animate={{ width: "100%" }}
                                    transition={{ duration: 2.6, ease: "easeInOut" }}
                                />
                            </div>
                        </motion.div>
                    )}

                    {step === 2 && (
                        <motion.div
                            key="step2"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, x: -24 }}
                            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                            data-testid="donation-step3"
                        >
                            <div className="flex items-center gap-3">
                                <CheckCircle2 className="h-8 w-8 text-teal" />
                                <p className="font-heading text-2xl font-extrabold tracking-tight">{c.step3Title}</p>
                            </div>
                            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                                KES {amount.toLocaleString()} — {c.step3Copy}
                            </p>
                            <div className="mt-5 rounded-md border border-dashed border-border bg-secondary/50 p-5 font-mono text-xs uppercase tracking-[0.14em] text-muted-foreground">
                                <div className="flex justify-between"><span>{c.receipt}</span><span className="text-foreground">#SY2027-0042</span></div>
                                <div className="mt-2 flex justify-between"><span>M-Pesa</span><span className="text-foreground">QHX7K2DEMO</span></div>
                                <div className="mt-2 flex justify-between"><span>Amount</span><span className="text-terracotta">KES {amount.toLocaleString()}</span></div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setStep(0)}
                                data-testid="donation-again-button"
                                className="mt-5 inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:border-primary hover:text-primary dark:hover:border-teal dark:hover:text-teal"
                            >
                                {c.donateAgain}
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>
                <p className="mt-6 border-t border-border pt-4 font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground" data-testid="donation-demo-note">
                    {c.demoNote}
                </p>
            </div>
        </div>
    );
};

const VolunteerDemo = ({ c }) => {
    const [name, setName] = useState("");
    const [ward, setWard] = useState("");
    const [joined, setJoined] = useState(false);
    const count = 62140 + (joined ? 1 : 0);

    return (
        <div className="overflow-hidden rounded-lg border border-border bg-card" data-testid="volunteer-demo">
            <div className="flex items-center justify-between border-b border-border bg-secondary/60 px-5 py-3">
                <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Volunteer CRM</p>
                <span className="font-mono text-[11px] uppercase tracking-wider text-teal">{count.toLocaleString()}</span>
            </div>
            <div className="p-6 sm:p-8">
                <AnimatePresence mode="wait">
                    {!joined ? (
                        <motion.form
                            key="form"
                            initial={{ opacity: 0, x: 24 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -24 }}
                            transition={{ duration: 0.35 }}
                            onSubmit={(e) => {
                                e.preventDefault();
                                if (name.trim() && ward.trim()) setJoined(true);
                            }}
                            className="space-y-4"
                        >
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder={c.volNamePh}
                                required
                                data-testid="volunteer-name-input"
                                className="w-full rounded-md border border-input bg-background px-4 py-3 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-primary dark:focus:border-teal"
                            />
                            <input
                                type="text"
                                value={ward}
                                onChange={(e) => setWard(e.target.value)}
                                placeholder={c.volWardPh}
                                required
                                data-testid="volunteer-ward-input"
                                className="w-full rounded-md border border-input bg-background px-4 py-3 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-primary dark:focus:border-teal"
                            />
                            <button
                                type="submit"
                                data-testid="volunteer-join-button"
                                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.98]"
                            >
                                {c.volButton}
                                <ArrowRight className="h-4 w-4" />
                            </button>
                        </motion.form>
                    ) : (
                        <motion.div
                            key="done"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                            className="py-4 text-center"
                            data-testid="volunteer-success"
                        >
                            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent">
                                <UserCheck className="h-6 w-6 text-primary dark:text-teal" />
                            </div>
                            <p className="mt-4 font-heading text-2xl font-extrabold tracking-tight">{c.volSuccess}</p>
                            <p className="mt-2 text-sm text-muted-foreground">
                                {name} · {ward} — {c.volSuccessCopy} <span className="font-semibold text-terracotta">#{count.toLocaleString()}</span>
                            </p>
                            <button
                                type="button"
                                onClick={() => {
                                    setJoined(false);
                                    setName("");
                                    setWard("");
                                }}
                                data-testid="volunteer-reset-button"
                                className="mt-5 text-sm font-semibold text-primary transition-colors hover:text-terracotta dark:text-teal"
                            >
                                ← {c.donateAgain}
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>
                <p className="mt-6 border-t border-border pt-4 font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{c.volNote}</p>
            </div>
        </div>
    );
};

const Campaigns = () => {
    const { t, lang } = useLang();
    const c = t.campaigns;

    useEffect(() => {
        document.title = lang === "sw" ? "Kampeni za Kisiasa — Hallomedics" : "Political Campaign Platforms — Hallomedics";
    }, [lang]);

    return (
        <div className="pt-28 lg:pt-36" data-testid="campaigns-page">
            {/* HERO */}
            <header className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-12">
                <div className="lg:col-span-7">
                    <p className="eyebrow flex items-center gap-2.5" data-testid="campaigns-eyebrow">
                        <span className="inline-block h-2 w-2 rounded-full bg-terracotta animate-pulse-dot" />
                        {c.eyebrow}
                    </p>
                    <h1 className="mt-6 font-heading text-4xl font-extrabold leading-[1.06] tracking-tight sm:text-5xl lg:text-6xl" data-testid="campaigns-heading" key={lang}>
                        <MaskedLine delay={0.1}>{c.h1a}</MaskedLine>
                        <MaskedLine delay={0.22}>
                            <span className="text-terracotta">{c.h1b}</span>
                        </MaskedLine>
                    </h1>
                    <Reveal delay={0.35}>
                        <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">{c.sub}</p>
                    </Reveal>
                    <Reveal delay={0.5}>
                        <div className="mt-9 flex flex-wrap items-center gap-4">
                            <Link
                                to="/contact?service=political-campaign"
                                data-testid="campaigns-cta-primary"
                                className="inline-flex items-center gap-2 rounded-full bg-terracotta px-7 py-3.5 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.98]"
                            >
                                {c.ctaPrimary}
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                            <Link
                                to="/portfolio"
                                data-testid="campaigns-cta-secondary"
                                className="inline-flex items-center gap-2 rounded-full border border-border px-7 py-3.5 text-sm font-semibold text-foreground transition-colors duration-200 hover:border-terracotta hover:text-terracotta"
                            >
                                {c.ctaSecondary}
                                <ArrowUpRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </Reveal>
                </div>
                <div className="lg:col-span-5">
                    <Reveal delay={0.25}>
                        <div className="group relative overflow-hidden rounded-md border border-border">
                            <img src={IMAGES.rally} alt="Political candidate addressing a public assembly" className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#091210]/60 to-transparent" aria-hidden="true" />
                            <div className="absolute bottom-4 left-4 rounded-full bg-background/85 px-4 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground backdrop-blur">
                                {c.imgChip}
                            </div>
                        </div>
                    </Reveal>
                </div>
            </header>

            {/* STATS */}
            <section className="mx-auto mt-16 max-w-7xl px-5 sm:px-8" data-testid="campaigns-stats">
                <div className="grid grid-cols-3 gap-px overflow-hidden rounded-md border border-border bg-border">
                    {STAT_VALUES.map((v, i) => (
                        <Reveal key={v} delay={i * 0.08}>
                            <div className="h-full bg-card px-4 py-8 text-center sm:px-6">
                                <p className="font-heading text-2xl font-extrabold tracking-tight text-terracotta sm:text-4xl" data-testid={`campaign-stat-${i}`}>{v}</p>
                                <p className="mt-1.5 text-xs text-muted-foreground sm:text-sm">{c.stats[i]}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* DONATION FUNNEL */}
            <section className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-24 sm:px-8 lg:grid-cols-12 lg:py-32" data-testid="funnel-section">
                <div className="lg:col-span-5">
                    <Reveal>
                        <p className="eyebrow">{c.funnelEyebrow}</p>
                        <h2 className="mt-4 font-heading text-2xl font-bold leading-snug tracking-tight sm:text-3xl lg:text-4xl">{c.funnelH2}</h2>
                        <p className="mt-5 text-base leading-relaxed text-muted-foreground">{c.funnelCopy}</p>
                    </Reveal>
                </div>
                <div className="lg:col-span-7">
                    <Reveal delay={0.15}>
                        <DonationDemo c={c} />
                    </Reveal>
                </div>
            </section>

            {/* VOLUNTEER TOOLS */}
            <section className="border-t border-border bg-secondary/40" data-testid="volunteer-section">
                <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-24 sm:px-8 lg:grid-cols-12 lg:py-32">
                    <div className="lg:col-span-7">
                        <Reveal>
                            <VolunteerDemo c={c} />
                        </Reveal>
                    </div>
                    <div className="lg:col-span-5">
                        <Reveal delay={0.1}>
                            <p className="eyebrow">{c.volEyebrow}</p>
                            <h2 className="mt-4 font-heading text-2xl font-bold leading-snug tracking-tight sm:text-3xl lg:text-4xl">{c.volH2}</h2>
                            <p className="mt-5 text-base leading-relaxed text-muted-foreground">{c.volCopy}</p>
                        </Reveal>
                    </div>
                </div>
                <div className="mx-auto grid max-w-7xl gap-5 px-5 pb-24 sm:grid-cols-2 sm:px-8 lg:grid-cols-4 lg:pb-32">
                    {c.tools.map((tool, i) => {
                        const Icon = TOOL_ICONS[i];
                        return (
                            <Reveal key={tool.title} delay={i * 0.08}>
                                <div className="h-full rounded-md border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-18px_rgba(15,76,58,0.35)]" data-testid={`volunteer-tool-${i}`}>
                                    <Icon className="h-6 w-6 text-terracotta" />
                                    <h3 className="mt-4 font-heading text-lg font-semibold tracking-tight">{tool.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{tool.copy}</p>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </section>

            {/* FEATURES */}
            <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32" data-testid="campaign-features">
                <Reveal>
                    <p className="eyebrow">{c.featuresEyebrow}</p>
                    <h2 className="mt-4 font-heading text-2xl font-bold leading-snug tracking-tight sm:text-3xl lg:text-4xl">{c.featuresH2}</h2>
                </Reveal>
                <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                    {c.features.map((f, i) => {
                        const Icon = FEATURE_ICONS[i];
                        return (
                            <Reveal key={f.title} delay={i * 0.08}>
                                <div className="h-full rounded-md border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-18px_rgba(15,76,58,0.35)]" data-testid={`campaign-feature-${i}`}>
                                    <Icon className="h-6 w-6 text-terracotta" />
                                    <h3 className="mt-4 font-heading text-lg font-semibold tracking-tight">{f.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.copy}</p>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
                <Reveal delay={0.3}>
                    <p className="mt-10 flex items-start gap-2.5 text-sm leading-relaxed text-muted-foreground" data-testid="campaigns-trust-note">
                        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-teal" />
                        {c.trustNote}
                    </p>
                </Reveal>
            </section>

            {/* CTA */}
            <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8 lg:pb-32" data-testid="campaigns-cta-section">
                <Reveal>
                    <div className="relative overflow-hidden rounded-lg bg-terracotta px-7 py-16 sm:px-12 lg:px-16 lg:py-20">
                        <div
                            aria-hidden="true"
                            className="pointer-events-none absolute inset-0 opacity-[0.12]"
                            style={{
                                backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)",
                                backgroundSize: "26px 26px",
                            }}
                        />
                        <div className="relative max-w-2xl">
                            <h2 className="font-heading text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">{c.ctaH2}</h2>
                            <p className="mt-5 text-base leading-relaxed text-white/80">{c.ctaCopy}</p>
                            <Link
                                to="/contact?service=political-campaign"
                                data-testid="campaigns-cta-button"
                                className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-terracotta transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.98]"
                            >
                                {c.ctaButton}
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </div>
                </Reveal>
            </section>
        </div>
    );
};

export default Campaigns;
